import {
  Box,
  BoxProps,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  SimpleGrid,
  VStack,
  chakra,
} from "@chakra-ui/react";
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AiOutlineEdit } from "react-icons/ai";
import { Article, useDataSource, valueArray } from "../../DataSource";
import { useResizeObserver } from "../../hooks/useResizeObserver";
import { CountsGrid } from "./CountsGrid";
import { AxisOptions, PopoverPath } from "./types";
import Heading from "../Heading";

interface ContentProps extends BoxProps {
  xAxis: string;
  yAxis: string;
  columns: Array<string>;
  onXAxisChange: (xAxis: string) => void;
  onYAxisChange: (xAxis: string) => void;
}

export default function Content(props: ContentProps): ReactElement {
  const { columns, xAxis, yAxis, onXAxisChange, onYAxisChange, ...rest } =
    props;

  const { articles, setFocused, setTempArticle } = useDataSource();

  const dataPairs = useMemo(
    () =>
      articles.flatMap((article) => {
        const xValues = article.data[xAxis];
        const yValues = article.data[yAxis];
        return xValues != null && yValues != null
          ? valueArray(xValues).flatMap((x) =>
              valueArray(yValues).map((y) => [x, y])
            )
          : [];
      }),
    [articles, xAxis, yAxis]
  );

  const axisOptions = useMemo((): AxisOptions => {
    const options = dataPairs.reduce(
      (acc: { x: Array<string>; y: Array<string> }, [xValue, yValue]) => ({
        x: acc.x.includes(xValue) ? acc.x : [...acc.x, xValue],
        y: acc.y.includes(yValue) ? acc.y : [...acc.y, yValue],
      }),
      { x: [], y: [] }
    );
    return { x: options.x.sort(), y: options.y.sort() };
  }, [dataPairs]);

  const dataCounts = useMemo(
    (): Record<string, Record<string, number>> =>
      dataPairs.reduce(
        (acc: Record<string, Record<string, number>>, [xValue, yValue]) => ({
          ...acc,
          [xValue]: {
            ...(acc[xValue] ?? {}),
            [yValue]: (acc[xValue]?.[yValue] ?? 0) + 1,
          },
        }),
        {}
      ),
    [dataPairs]
  );

  const [countsRef, countsRect] = useResizeObserver<HTMLDivElement>();

  const xOptionsRef = useRef<HTMLDivElement>(null);

  const [popoverPath, setPopoverPath] = useState<PopoverPath | null>(null);

  const onPopoverPathChange = useCallback(
    (xOption: string, yOption: string) => {
      const relevantArticles = articles.filter(
        (article) =>
          valueArray(article.data[xAxis]).includes(xOption) &&
          valueArray(article.data[yAxis]).includes(yOption)
      );
      if (relevantArticles.length > 1) {
        setPopoverPath({
          x: { option: xOption, index: axisOptions.x.indexOf(xOption) },
          y: { option: yOption, index: axisOptions.y.indexOf(yOption) },
          articles: relevantArticles,
        });
      } else if (relevantArticles.length === 1) {
        setFocused(relevantArticles[0]);
      }
    },
    [articles, axisOptions.x, axisOptions.y, setFocused, xAxis, yAxis]
  );

  useEffect(() => {
    if (xOptionsRef.current != null) {
      const spans = [...xOptionsRef.current.querySelectorAll("span").values()];
      const maxHeight = spans.reduce(
        (acc, span) => Math.max(acc, span.getBoundingClientRect().height),
        0
      );
      xOptionsRef.current.style.height = `${Math.ceil(maxHeight)}px`;
    }
  }, [articles, xAxis, yAxis]);

  const hasArticles = useMemo(() => dataPairs.length > 0, [dataPairs.length]);

  const handleArticleChange = useCallback(
    (article: Article) => {
      setFocused(article);
      setTempArticle(article);
    },
    [setFocused, setTempArticle]
  );

  return !hasArticles ? (
    <Flex w="100%" justify="center" align="center" {...rest}>
      <Heading level={5}>
        <chakra.em>No articles to display</chakra.em>
      </Heading>
    </Flex>
  ) : (
    <HStack
      spacing="0.5em"
      align="flex-start"
      justify="center"
      {...rest}
      fontSize="clamp(8px, 5vmin, 18px)"
    >
      <HStack
        spacing="0.25em"
        h={countsRect != null ? `${countsRect.height}px` : undefined}
        w="fit-content"
      >
        <HStack justify="center" spacing="0.5em" w="fit-content">
          <chakra.u
            textAlign="center"
            flexShrink="1"
            flexGrow="1"
            whiteSpace="nowrap"
          >
            {yAxis}
          </chakra.u>
          <Menu placement="start-end">
            <MenuButton
              flexShrink="0"
              flexGrow="0"
              as={IconButton}
              aria-label="Select vertical column"
              variant="ghost"
              size="xs"
              icon={<Icon as={AiOutlineEdit} w="1.25em" h="1.25em" />}
            />
            <Portal>
              <MenuList
                zIndex="popover"
                maxH="56"
                overflowY="auto"
                fontSize="md"
              >
                {columns.map((column) => (
                  <MenuItem
                    key={column}
                    onClick={() => onYAxisChange(column)}
                    bg={yAxis === column ? "gray.200" : undefined}
                  >
                    {column}
                  </MenuItem>
                ))}
              </MenuList>
            </Portal>
          </Menu>
        </HStack>
        <SimpleGrid
          alignSelf="stretch"
          spacing="0"
          justifyItems="stretch"
          alignItems="center"
          maxH="100%"
        >
          {axisOptions.y.map((option) => (
            <chakra.span
              key={option}
              textAlign="end"
              w="100%"
              whiteSpace="nowrap"
            >
              {option}
            </chakra.span>
          ))}
        </SimpleGrid>
      </HStack>

      <VStack align="stretch" spacing="0.5em">
        <CountsGrid
          xAxis={xAxis}
          yAxis={yAxis}
          gridRef={countsRef}
          gridRect={countsRect}
          counts={dataCounts}
          axisOptions={axisOptions}
          popoverPath={popoverPath}
          onClose={() => setPopoverPath(null)}
          onPopoverPathChange={onPopoverPathChange}
          onArticleChange={handleArticleChange}
        />
        <VStack
          spacing="0"
          w={countsRect != null ? `${countsRect.width}px` : undefined}
        >
          <HStack
            ref={xOptionsRef}
            spacing="0"
            align="flex-start"
            justify="space-around"
            w="100%"
          >
            {axisOptions.x.map((option, i) => (
              <Box key={option} position="relative" w="1px" h="1px">
                <chakra.span
                  position="absolute"
                  top="0.25em"
                  right="0.25em"
                  textAlign="end"
                  transformOrigin="calc(100% - 1ch) 0"
                  transform="rotate(-45deg)"
                  whiteSpace="nowrap"
                >
                  {option}
                </chakra.span>
              </Box>
            ))}
          </HStack>

          <HStack w="100%" justify="center" spacing="0.5em">
            <chakra.u>{xAxis}</chakra.u>
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                aria-label="Select horizontal column"
                variant="ghost"
                size="xs"
                icon={<Icon as={AiOutlineEdit} w="1.25em" h="1.25em" />}
              />
              <MenuList
                zIndex="popover"
                maxH="56"
                overflowY="auto"
                fontSize="md"
              >
                {columns.map((column) => (
                  <MenuItem
                    key={column}
                    onClick={() => onXAxisChange(column)}
                    bg={xAxis === column ? "gray.200" : undefined}
                  >
                    {column}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </HStack>
        </VStack>
      </VStack>
    </HStack>
  );
}
